import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/members/user/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth.http.service';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { LoginType, UserContext } from "../models/members/user/user-context.model"
import { ToasterNotificationService } from './toaster-notification.service';
import { CreateUserInfoModel } from '../models/members/user/create-user-info.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalComponent } from 'src/app/global-component';
import { CreateClientInfoModel } from '../models/members/client/create-client-info.model';
import { ClientModel } from '../models/members/client/client.model';
import { LoginProviderEnum } from '../enums/login-provider-enum';
import { Roles } from '../security/roles';

export type UserType = UserModel | undefined;
export type UserContextType = UserContext | undefined;

/**
 * The authentication data services.
 * This service provides current logged in User Context such as Id and Email and also provides access to 
 * the users authorization context.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthMyfileService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}-auth`;
  ucLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}-uc`;

  // current User Context
  public currentUserContext$: Observable<UserContextType>;
  private currentUserContextSubject: BehaviorSubject<UserContextType>;

  public isLoading$: Observable<boolean>;
  private isLoadingSubject: BehaviorSubject<boolean>;

  private loggedIn: BehaviorSubject<boolean>;
  public loggedIn$: Observable<boolean>;

  private authModel: AuthModel | undefined = undefined;

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    protected route: ActivatedRoute,
    private modalServices: NgbModal,
    private toasterNotificationService: ToasterNotificationService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();

    this.currentUserContextSubject = new BehaviorSubject<UserContextType>(undefined);
    this.currentUserContext$ = this.currentUserContextSubject.asObservable();

    this.loggedIn = new BehaviorSubject(this.getAuthFromLocalStorage() != undefined ? true : false);
    this.loggedIn$ = this.loggedIn.asObservable();
  }

  get currentUserContextValue(): UserContextType {

    if (!this.currentUserContextSubject.value) {
      const userContext = this.getUserContextFromLocalStorage();

      if (userContext != null) {
        this.currentUserContextSubject.next(userContext);
      }
    }

    return this.currentUserContextSubject.value;
  }

  isInRole(role: string): boolean {
    let hasRole = false;
    const authContext = this.currentUserContextValue?.authContext;

    if (role && authContext) {
      let roles = authContext?.roles;

      if (roles) {
        hasRole = roles.indexOf(role) > -1;
      }
    }

    return hasRole;
  }

  loginTypeIsUser(): boolean {
    if (this.currentUserContextValue && this.currentUserContextValue.loginType === LoginType.User) {
      return true;
    }
    return false;
  }

  canAccessOperation(operation: string): boolean {
    const authContext = this.currentUserContextValue?.authContext;

    let canAccess = false;

    if (operation && authContext) {
      let ops = authContext?.authorizedOperations?.operations;

      if (ops) {
        canAccess = ops.indexOf(operation) > -1;
      }
    }

    return canAccess;
  }

  // public methods
  login(email: string, password: string, loginType: LoginType, tenantId?: string, socialId?: string, loginProvider?: LoginProviderEnum, accessCode?: string): Observable<UserContextType> {

    this.isLoadingSubject.next(true);

    return this.authHttpService.login(email, password, loginType, tenantId, socialId, loginProvider, accessCode)
      .pipe(

        map((auth: AuthModel) => {
          this.authModel = auth;
          if (auth.isMultiFactorAuthenticationEnabled !== true) {
            this.setAuthToLocalStorage(auth);
            this.loggedIn.next(true);
            return true;
          }
          return undefined;

        }),

        switchMap(() => this.getUserContext()),

        catchError((err) => {
          var title = "Sign in failed";

          if (err.status === 401 || err.error && err.status != 0) {
            //return JSON.parse(err.error); //we can return Error Model to view
            this.toasterNotificationService.showError(err.error, title);
          }
          else {
            if (loginProvider) {
              this.toasterNotificationService.showError("Social login could not be verified. Please try again later.", "Sign in failed");
            } else {
              this.toasterNotificationService.showError("Invalid email or password. Please try again later.", "Sign in failed");
            }
          }

          //api responds with 401 Not Authorized. Return undefined to view
          //returning undefined will continue to subscriber
          return of(undefined);
        }),

        //catchError(this.handleError),

        finalize(() => {
          this.isLoadingSubject.next(false);
        })
      );
  }

  getUserFromTenantList(username: string, loginType: LoginType, disableErrorMessage?: boolean): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.getUsersTenantList(username, loginType).pipe(
      map((tenantList: any) => {
        return tenantList;
      }),

      catchError((err) => {
        if (disableErrorMessage === false || disableErrorMessage === undefined) {
          if (err.error && err.status != 0) {
            var title = "Sign in failed";
            this.toasterNotificationService.showError(err.error, title);
          }
          else {
            this.toasterNotificationService.showError("Something went wrong, please try again later", "Server Error");
          }
        }
        return of(undefined);
      }),

      finalize(() => {
        this.isLoadingSubject.next(false);
      }));
  }

  logout(redirectUrl?: string) {
    //add check to see if token is valid/exists 
    if (redirectUrl) {
      redirectUrl = decodeURIComponent(redirectUrl);
    }

    if (this.currentUserContextValue) {

      this.authHttpService.logout().subscribe(() => {

        var loginType = this.currentUserContextValue?.loginType;

        localStorage.removeItem(this.authLocalStorageToken);
        localStorage.removeItem(this.ucLocalStorageToken);

        this.loggedIn.next(false);
        this.currentUserContextSubject.next(undefined);
        this.modalServices.dismissAll();
        if (redirectUrl != undefined) {
          this.router.navigate([loginType ? GlobalComponent.authRedirectUrls.clientSignIn : GlobalComponent.authRedirectUrls.userSignIn], { queryParams: { redirectURL: redirectUrl } });
        } else {
          this.router.navigate([loginType ? GlobalComponent.authRedirectUrls.clientSignIn : GlobalComponent.authRedirectUrls.userSignIn], { queryParams: {} });
        }

      });
    } else {
      if (redirectUrl != undefined) {
        this.router.navigate([GlobalComponent.authRedirectUrls.userSignIn], { queryParams: { redirectUrl: redirectUrl } });
      } else {
        this.router.navigate([GlobalComponent.authRedirectUrls.userSignIn], { queryParams: {} });
      }
    }
  }

  ping(): Observable<boolean> {
    return this.authHttpService.pingSystem();
  }

  refreshAuthModel(): Observable<AuthModel | undefined> {
    const authModel = this.getAuthFromLocalStorage();

    if (authModel == null) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);

    return this.authHttpService.refreshToken(authModel)
      .pipe(
        map((auth: AuthModel) => {
          this.setAuthToLocalStorage(auth);
          return auth;
        }),

        catchError((err) => {
          localStorage.removeItem(this.authLocalStorageToken);
          this.loggedIn.next(false);
          return of(undefined);
        }),

        finalize(() => {
          this.isLoadingSubject.next(false);
        }));
  }

  public getUserContext(): Observable<any> {

    const auth = this.getAuthFromLocalStorage();
    if (this.authModel?.isMultiFactorAuthenticationEnabled === true) {
      return of(this.authModel);
    }
    if (!auth || !auth.jwtAccessToken || !auth.jwtAccessToken.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);

    return this.authHttpService.getUserContext()
      .pipe(

        map((userContext: UserContextType) => {
          if (userContext) {
            this.setUserContextToLocalStorage(userContext);
            this.currentUserContextSubject.next(userContext);
          } else {
            this.logout();
          }
          return userContext;
        }),

        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  registerUser(user: CreateUserInfoModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.signUpUser(user)
      .pipe(
        map((result: UserModel | undefined) => {
          return result;
        }),
        catchError((err) => {
          console.error('err', err);
          if (err.status != 0) {
            if (err.error.message) {
              this.toasterNotificationService.showError(err.error.message, "Sign up failed");
            } else {
              this.toasterNotificationService.showError(err.message, "Sign up failed");
            }
          }
          else {
            this.toasterNotificationService.showError("Something went wrong. Please try again later.", "Sign up failed");
          }
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  registerClient(client: CreateClientInfoModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.signUpClient(client)
      .pipe(
        map((result: ClientModel | undefined) => {
          return result;
        }),
        catchError((err) => {
          console.error('err', err);
          if (err.status != 0) {
            if (err.error.message) {
              this.toasterNotificationService.showError(err.error.message, "Sign up failed");
            } else {
              this.toasterNotificationService.showError(err.message, "Sign up failed");
            }
          }
          else {
            this.toasterNotificationService.showError("Something went wrong. Please try again later.", "Sign up failed");
          }
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  forgotPassword(email: string, loginType: LoginType, tenantId?: string): Observable<boolean | undefined> {

    this.isLoadingSubject.next(true);

    return this.authHttpService.forgotPassword(email, loginType, tenantId)
      .pipe(

        map((res) => {
          return true;
        }),
        catchError((err) => {
          var title = "Password reset failed";

          if (err.status === 401) {
            // auto logout if 401 response returned from api
            this.toasterNotificationService.showError(err.error, title);
          }

          if (err.error && err.status != 0) {
            //return JSON.parse(err.error); //we can return Error Model to view
            this.toasterNotificationService.showError(err.error, title);
          }
          else {
            this.toasterNotificationService.showError("Something went wrong. Please try again later.", title);
          }

          //api responds with 401 Not Authorized. Return undefined to view
          //returning undefined will continue to subscriber
          return of(err);
        }),

        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  // private methods
  private setAuthToLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/expiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.jwtAccessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private setUserContextToLocalStorage(userContext: UserContext): boolean {
    // store userContext to keep user logged in between page refreshes
    if (userContext) {
      localStorage.setItem(this.ucLocalStorageToken, JSON.stringify(userContext));
      return true;
    }
    return false;
  }

  public getAuthFromLocalStorage(): AuthModel | undefined {
    try {

      const lsValue = localStorage.getItem(this.authLocalStorageToken);

      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      return undefined;
    }
  }

  private getUserContextFromLocalStorage(): UserContextType {
    try {

      const lsValue = localStorage.getItem(this.ucLocalStorageToken);

      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue);
    } catch (error) {
      return undefined;
    }
  }

  private handleError(err: any) {

    console.error('err', err);

    if (err != undefined && err.status != 0 && err.error) {
      // if (err.error instanceof ErrorModel) 
      // {
      //   this.toasterNotificationService.showError(err.error.message, "System");
      // }
      // else {
      this.toasterNotificationService.showError(err.error, "System");
      // }
      //return JSON.parse(err.error);
    }
    else {
      this.toasterNotificationService.showError("Something went wrong. Please try again later.", "System");
    }

    //returning undefined will continue to subscriber
    return of(undefined);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}